import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import DashboardLayout from '@/pages/dashboard/components/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/lib/supabase';

interface EnrollmentRow {
  id: string;
  user_id: string;
  created_at: string;
  profiles?: { full_name?: string | null; email?: string | null } | null;
}

interface CourseRow {
  id: string;
  title: string;
  instructor: string;
}

function CourseEnrollments() {
  const { user } = useAuth();
  const { id } = useParams<{ id: string }>();
  const [course, setCourse] = useState<CourseRow | null>(null);
  const [enrollments, setEnrollments] = useState<EnrollmentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async () => {
    if (!user || !id) return;
    setLoading(true);
    setError('');
    try {
      const { data: courseData, error: courseErr } = await supabase
        .from('courses')
        .select('id, title, instructor')
        .eq('id', id)
        .maybeSingle();
      if (courseErr) throw courseErr;
      setCourse(courseData as CourseRow | null);

      const { data: enrolls, error: enrollErr } = await supabase
        .from('enrollments')
        .select('id, user_id, created_at, profiles(full_name, email)')
        .eq('course_id', id)
        .order('created_at', { ascending: false });
      if (enrollErr) throw enrollErr;
      setEnrollments((enrolls as EnrollmentRow[]) || []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Impossible de charger les inscriptions.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, id]);

  return (
    <DashboardLayout>
      <div className="flex items-start justify-between mb-8 gap-4 flex-wrap">
        <div>
          <Link
            to="/dashboard/courses"
            className="inline-flex items-center gap-1 text-sm text-foreground-400 hover:text-primary-400 transition-colors mb-2"
          >
            <i className="ri-arrow-left-line" />
            Mes formations
          </Link>
          <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground-50">
            Inscriptions reçues
          </h1>
          {course && (
            <p className="text-sm text-foreground-400 mt-1">
              {course.title} · {course.instructor}
            </p>
          )}
        </div>
        <span className="px-3 py-1.5 rounded-full bg-primary-500/10 text-primary-400 text-sm font-semibold whitespace-nowrap">
          {enrollments.length} inscrit{enrollments.length === 1 ? '' : 's'}
        </span>
      </div>

      {error && (
        <div className="p-3 rounded-lg bg-red-500/10 border border-red-400/30 text-sm text-red-400 mb-4">
          {error}
          <button onClick={load} className="ml-3 underline text-foreground-50">Réessayer</button>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-500/10">
            <i className="ri-loader-4-line text-2xl text-primary-400 animate-spin" />
          </div>
        </div>
      ) : enrollments.length === 0 ? (
        <div className="text-center py-16">
          <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-primary-500/10">
            <i className="ri-user-line text-3xl text-primary-400" />
          </div>
          <h2 className="font-heading font-bold text-xl text-foreground-50 mb-2">Aucune inscription</h2>
          <p className="text-sm text-foreground-400">
            Personne ne s&apos;est encore inscrit à cette formation.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {enrollments.map((enroll, index) => {
            const name = enroll.profiles?.full_name || 'Apprenant';
            const email = enroll.profiles?.email || '—';
            return (
              <div
                key={enroll.id}
                className="p-5 rounded-xl bg-background-100 border border-background-200/70 flex items-center justify-between gap-4 flex-wrap"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary-500/10 flex items-center justify-center shrink-0">
                    <i className="ri-user-3-line text-primary-400" />
                  </div>
                  <div>
                    <p className="font-medium text-foreground-50">{name}</p>
                    <p className="text-sm text-foreground-400">{email}</p>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-accent-500/10 text-accent-400 whitespace-nowrap">
                    Inscrit(e)
                  </span>
                  <p className="text-xs text-foreground-500 mt-1">
                    {new Date(enroll.created_at).toLocaleDateString('fr-FR')}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </DashboardLayout>
  );
}

export default CourseEnrollments;